import React, { useState, useEffect, useCallback, useMemo, useRef, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import Swal from "sweetalert2";
import {
  addEditCategory,
  deleteCategory,
} from "./../../actions/categoryActions";
import { apiV2 } from "../../actions/index";
import {
  uploadImage,
  uploadCategoryImage,
  clearMedia,
} from "./../../actions/mediaActions";
import LoadingBackdrop from "../Common/LoadingBackdrop";
import ImageContainerPreview from '../Common/ImageContainerPreview';
import SortableItemList from "../Common/SortableItemList";
import { INITIAL_LIST_STATE, createSortHandlers } from "../Common/sortableListUtils";
import MainContentContainer from "../MainContentContainer/MainContentContainer";
import { preventEnterFormSubmission } from '../../utils/formUtils';
import UniversalLinkField from '../Common/UniversalLinkField';

function AddEditCategory({ isEdit, selectedId }, ref) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backPath = searchParams.get('from') === 'audiobooks' ? '/audiobooks' : '/category';
  const formRef = useRef();

  // Expose the submit handler to parent
  useImperativeHandle(ref, () => ({
    submit: () => {
      handleSubmit({ preventDefault: () => { } });
    }
  }));

  const [state, setState] = useState({
    category_title: "",
    category_is_active: true,
    child_objects: [],
    session_data: [],
    isSetToEdit: false,
    category_image_preview: null,
    category_image_url: "",
    category_image: "",
    category_is_dark_mode: false,
    category_is_quick_tap: false,
    category_is_select_interest: true,
    category_explore_term: "",
    category_deep_link_image: "",
    deep_link_image_preview: "",
  });

  // Keep series state for later use
  const [seriesState, setSeriesState] = useState(INITIAL_LIST_STATE);

  // Add subcategories state
  const [subcategoriesState, setSubcategoriesState] = useState(INITIAL_LIST_STATE);

  // Add sessions state
  const [sessionsState, setSessionsState] = useState(INITIAL_LIST_STATE);

  // Add quick taps state
  const [quickTapsState, setQuickTapsState] = useState(INITIAL_LIST_STATE);



  const [isLoading, setIsLoading] = useState(false);



  // Add session data state
  const [sessionData, setSessionData] = useState({});

  // Add session data handler
  const handleSessionDataChange = useCallback((id, data) => {
    setSessionData(prev => ({
      ...prev,
      [id]: {
        is_featured: data.is_featured ?? 0,
        is_dark_mode: data.is_dark_mode ?? 0
      }
    }));
  }, []);



  // Fetch category data on mount/ID change
  useEffect(() => {
    if (!isEdit) return;

    setIsLoading(true);
    apiV2.get(`/categories/${selectedId}`)
      .then((response) => {
        const category = response.data;
        setState(prev => ({
          ...prev,
          ...category,
          isSetToEdit: true,
        }));

        // Process subcategories
        if (category.subcategories) {
          const subcategoriesItems = category.subcategories.map(obj => ({
            id: obj.subcategory_id + "SUBCATEGORY",
            label: obj.subcategory_title,
            value: obj.subcategory_id + ""
          }));
          setSubcategoriesState({
            items: subcategoriesItems,
            isLoaded: true,
            sortOrder: category.subcategories.map(obj => obj.subcategory_id + ""),
            isLoading: false
          });
        }

        // Process series
        if (category.series) {
          const seriesItems = category.series.map(obj => ({
            id: obj.series_id + "SERIES",
            label: obj.series_title,
            value: obj.series_id + ""
          }));
          setSeriesState({
            items: seriesItems,
            isLoaded: true,
            sortOrder: category.series.map(obj => obj.series_id + ""),
            isLoading: false
          });
        }

        // Process sessions
        if (category.sessions) {
          const sessionsItems = category.sessions.map(obj => ({
            id: obj.session_id + "SESSION",
            label: obj.session_name,
            value: obj.session_id + ""
          }));
          const initialSessionData = category.sessions.reduce((acc, session) => {
            acc[session.session_id + ""] = {
              is_featured: session.session_is_featured || 0,
              is_dark_mode: session.session_is_dark_mode || 0
            };
            return acc;
          }, {});
          setSessionData(initialSessionData);
          setSessionsState({
            items: sessionsItems,
            isLoaded: true,
            sortOrder: category.sessions.map(obj => obj.session_id + ""),
            isLoading: false
          });
        }

        // Process quick taps
        if (category.quick_taps && category.category_is_quick_tap) {
          const quickTapsItems = category.quick_taps.map(obj => ({
            id: obj.id + "QUICKTAP",
            label: obj.quick_tap_title,
            value: obj.id + ""
          }));
          setQuickTapsState({
            items: quickTapsItems,
            isLoaded: true,
            sortOrder: category.quick_taps.map(obj => obj.id + ""),
            isLoading: false
          });
        }

        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching category:', error);
        setIsLoading(false);
      });
  }, [isEdit, selectedId]);



  const handleFieldChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (state.category_image_preview) {
      window.URL.revokeObjectURL(state.category_image_preview);
    }
    setState(prev => ({
      ...prev,
      category_image_preview: window.URL.createObjectURL(file),
    }));
    dispatch(uploadCategoryImage(selectedId, file))
      .then((response) => {
        setState(prev => ({
          ...prev,
          category_image: response.data.image_url,
        }));
        dispatch(clearMedia());
      });
  };

  const handleDeepLinkImageUpload = (e) => {
    const file = e.target.files[0];
    if (state.deep_link_image_preview) {
      window.URL.revokeObjectURL(state.deep_link_image_preview);
    }
    setState(prev => ({
      ...prev,
      deep_link_image_preview: window.URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file))
      .then((response) => {
        setState(prev => ({
          ...prev,
          category_deep_link_image: response.data.image_url,
        }));
        dispatch(clearMedia());
      });
  };

  // Keep the cleanup effect for unmounting
  useEffect(() => {
    return () => {
      // Clear any image previews
      if (state.category_image_preview) {
        window.URL.revokeObjectURL(state.category_image_preview);
      }
      if (state.deep_link_image_preview) {
        window.URL.revokeObjectURL(state.deep_link_image_preview);
      }
    };
  }, [state.category_image_preview, state.deep_link_image_preview]);

  // Update success handler in handleSubmit
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!state.category_title) {
      Swal.fire({
        icon: "error",
        text: "Category name is required",
        showConfirmButton: true,
        showCancelButton: false,
        confirmButtonText: "Ok",
      });
      return;
    }

    // Validate Quick Tap and Select Interest combination
    if (state.category_is_quick_tap && state.category_is_select_interest) {
      Swal.fire({
        icon: "warning",
        text: "Quick Tap Categories cannot be in the Select Interest section",
        showConfirmButton: true,
        showCancelButton: false,
        confirmButtonText: "Ok",
      });
      return;
    }

    // Show loading state
    Swal.fire({
      title: "Saving...",
      animation: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
    });

    // Parse all states into child_objects_json
    const parseChildObjects = () => {
      const child_objects_json = [];
      let sortNumber = 1;

      // Parse series
      seriesState.sortOrder.forEach(id => {
        if (!id) return; // Skip empty selections
        child_objects_json.push({
          object_id: id,
          is_sub: 0,
          is_series: 1,
          is_quick_tap: 0,
          sort_number: sortNumber
        });
        sortNumber++;
      });

      // Parse subcategories
      subcategoriesState.sortOrder.forEach(id => {
        if (!id) return; // Skip empty selections
        child_objects_json.push({
          object_id: id,
          is_sub: 1,
          is_series: 0,
          is_quick_tap: 0,
          sort_number: sortNumber
        });
        sortNumber++;
      });

      // Parse sessions or quick taps based on category_is_quick_tap
      if (state.category_is_quick_tap) {
        quickTapsState.sortOrder.forEach(id => {
          if (!id) return; // Skip empty selections
          child_objects_json.push({
            object_id: id,
            is_sub: 0,
            is_series: 0,
            is_quick_tap: 1,
            sort_number: sortNumber,
            session_data: {
              session_is_featured: sessionData[id]?.is_featured || 0,
              session_is_dark_mode: sessionData[id]?.is_dark_mode || 0
            }
          });
          sortNumber++;
        });
      } else {
        sessionsState.sortOrder.forEach(id => {
          if (!id) return; // Skip empty selections
          const sessionDataForId = sessionData[id] || {};

          child_objects_json.push({
            object_id: id,
            is_sub: 0,
            is_series: 0,
            is_quick_tap: 0,
            sort_number: sortNumber,
            session_data: {
              session_is_featured: sessionDataForId.is_featured || 0,
              session_is_dark_mode: sessionDataForId.is_dark_mode || 0
            }
          });
          sortNumber++;
        });
      }

      return child_objects_json;
    };

    // Prepare the category data
    const categoryData = {
      ...state,
      subcategories: [],
      series: [],
      sessions: [],
      quick_taps: [],
      decks: [],
      // Ensure boolean values are properly formatted
      category_is_active: state.category_is_active ? 1 : 0,
      category_is_dark_mode: state.category_is_dark_mode ? 1 : 0,
      category_is_quick_tap: state.category_is_quick_tap ? 1 : 0,
      category_is_select_interest: state.category_is_select_interest ? 1 : 0,
    };

    // Get the parsed child objects
    const child_objects_json = parseChildObjects();

    // Dispatch the action with the complete data
    dispatch(addEditCategory({
      category_json: JSON.stringify(categoryData),
      child_objects_json: JSON.stringify(child_objects_json)
    }))
      .then((response) => {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: `Category ${isEdit ? 'updated' : 'created'} successfully`,
          showConfirmButton: true,
          confirmButtonText: "Ok",
        }).then(() => {
          navigate(backPath);
        });
      })
      .catch((error) => {
        console.error('Error saving category:', error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.message || "Failed to save category",
          showConfirmButton: true,
          confirmButtonText: "Ok",
        });
      });
  };

  const handleDelete = (e) => {
    e.preventDefault();
    Swal.fire({
      icon: "warning",
      text: "Are you sure you want to delete this category?",
      showConfirmButton: true,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "Delete Category",
    }).then((result) => {
      if (result.value) {
        dispatch(deleteCategory(state.category_id, () => navigate(backPath)));
      }
    });
  };

  // Sort/remove handlers via factory (setState is stable, so useMemo with [] is safe)
  const seriesHandlers = useMemo(() => createSortHandlers(setSeriesState), []);
  const subcategoriesHandlers = useMemo(() => createSortHandlers(setSubcategoriesState), []);
  const sessionsHandlers = useMemo(() => createSortHandlers(setSessionsState), []);
  const quickTapsHandlers = useMemo(() => createSortHandlers(setQuickTapsState), []);

  // Handle quick tap switch change
  const handleQuickTapChange = useCallback((e) => {
    const newValue = e.target.checked;

    // Update the quick tap state
    setState(prev => ({ ...prev, category_is_quick_tap: newValue }));
  }, []);

  if (isEdit && (isLoading || !state.isSetToEdit)) {
    return <LoadingBackdrop open={true} />;
  }

  return (
    <MainContentContainer component="form" ref={formRef} onSubmit={handleSubmit} onKeyDown={preventEnterFormSubmission} autoComplete="off">
      < Box display="grid" gap={4} >
        {/* Top row: 3 columns on desktop */}
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
          <Box>
            <TextField
              fullWidth
              label="Category Name"
              name="category_title"
              value={state.category_title}
              onChange={handleFieldChange}
              required
            />
          </Box>
          <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2}>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    name="category_is_active"
                    checked={state.category_is_active}
                    onChange={(e) => setState(prev => ({ ...prev, category_is_active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    name="category_is_select_interest"
                    checked={state.category_is_select_interest}
                    onChange={(e) => setState(prev => ({ ...prev, category_is_select_interest: e.target.checked }))}
                  />
                }
                label="Appear in Select Interest"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    name="category_is_dark_mode"
                    checked={state.category_is_dark_mode}
                    onChange={(e) => setState(prev => ({ ...prev, category_is_dark_mode: e.target.checked }))}
                  />
                }
                label="Dark Mode"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    name="category_is_quick_tap"
                    checked={state.category_is_quick_tap}
                    onChange={handleQuickTapChange}
                  />
                }
                label="Quick Tap"
              />
            </Box>
          </Box>
        </Box>
        <Box>
          <TextField
            fullWidth
            label="Description"
            name="category_description"
            value={state.category_description}
            onChange={handleFieldChange}
            multiline
            minRows={2}
          />
        </Box>
        <Box>
          <TextField
            fullWidth
            label="Explore More Search Term"
            name="category_explore_term"
            value={state.category_explore_term}
            onChange={handleFieldChange}
          />
        </Box>
        {/* Category Image and Share Settings side by side */}
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 2fr' }} gap={3}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Category Image
            </Typography>
            <ImageContainerPreview
              title="Category Image"
              image={state.category_image_preview || state.category_image}
              name="category_image"
              handleRemove={() => setState(prev => ({ ...prev, category_image: "", category_image_preview: null }))}
              handleImageUpload={handleImageUpload}
              showCopyUrlButton={true}
              sx={{ padding: 1, background: '#FFFCF5', height: 90, objectFit: 'contain' }}
            />
          </Box>
          <Box>
            <Typography variant="h6" gutterBottom>
              Share Settings
            </Typography>
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
              <Box>
                <ImageContainerPreview
                  title="Deep Link Image"
                  image={state.deep_link_image_preview || state.category_deep_link_image}
                  name="deep_link_image"
                  handleRemove={() => setState(prev => ({ ...prev, category_deep_link_image: "", deep_link_image_preview: null }))}
                  handleImageUpload={handleDeepLinkImageUpload}
                  showCopyUrlButton={true}
                  sx={{ padding: 1, background: '#FFFCF5', height: 90, objectFit: 'contain' }}
                />
              </Box>
              <Box>
                <UniversalLinkField url={state.universal_link} />
              </Box>
            </Box>
          </Box>
        </Box>

        {
          isEdit && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" gutterBottom>
                Subitems
              </Typography>

              {/* Series Section */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Series
                </Typography>
                {seriesState.isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <SortableItemList
                    items={seriesState.items}
                    sortOrder={seriesState.sortOrder}
                    onRemove={seriesHandlers.onRemove}
                    onSortEnd={seriesHandlers.onSort}
                  />
                )}
              </Box>

              {/* Subcategories Section */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Subcategories / Collections
                </Typography>
                {subcategoriesState.isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <SortableItemList
                    items={subcategoriesState.items}
                    sortOrder={subcategoriesState.sortOrder}
                    onRemove={subcategoriesHandlers.onRemove}
                    onSortEnd={subcategoriesHandlers.onSort}
                  />
                )}
              </Box>

              {/* Sessions or Quick Taps Section */}
              {state.category_is_quick_tap ? (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Quick Taps
                  </Typography>
                  {quickTapsState.isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <SortableItemList
                      items={quickTapsState.items}
                      sortOrder={quickTapsState.sortOrder}
                      onRemove={quickTapsHandlers.onRemove}
                      onSortEnd={quickTapsHandlers.onSort}
                      showSwitches={true}
                      onSessionDataChange={handleSessionDataChange}
                      sessionData={sessionData}
                    />
                  )}
                </Box>
              ) : (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Sessions
                  </Typography>
                  {sessionsState.isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <SortableItemList
                      items={sessionsState.items}
                      sortOrder={sessionsState.sortOrder}
                      onRemove={sessionsHandlers.onRemove}
                      onSortEnd={sessionsHandlers.onSort}
                      showSwitches={true}
                      onSessionDataChange={handleSessionDataChange}
                      sessionData={sessionData}
                    />
                  )}
                </Box>
              )}
            </Box>
          )
        }
        {
          isEdit && (
            <Box sx={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
              >
                Delete Category
              </Button>
            </Box>
          )
        }
      </Box >
    </MainContentContainer >
  );
}

export default forwardRef(AddEditCategory);
